<script setup lang="ts">
  import { computed, nextTick, onMounted, useTemplateRef } from 'vue'
  import { InputGroupDate } from '@gat-ship/vue-components'
  import { EstimatedTimePickerCombined } from '@gat-ship/ref-data'
  import type { EstimatedTimeChoice } from '@gat-ship/ref-data'
  import { IconCheck, IconInfo, IconWarning } from '@gat-ship/icons'
  import type { EtaFieldValue } from '../../../../model/eta-bridge'
  import type { ArrivalRequirementState } from '../../../../model/portcall-wizard'
  import { useEtaField } from '../../../../composables/useEtaField'

  /**
   * The arrival-step body: a typed date field beside a combined
   * estimated-time input — type a clock time for an exact ETA or pick a named
   * window — with a requirement banner at the foot, shown only for
   * pilot-station ports where the pilot/port arrival relationship needs
   * explaining. Edits an {@link EtaFieldValue} (via the shared
   * `timeChoiceFromEtaField` / `etaPatchFromTimeChoice` bridge), so
   * `getValues()` resolves it identically.
   */
  const props = withDefaults(
    defineProps<{
      modelValue: EtaFieldValue
      disabled?: boolean
      futureDate?: boolean
      /** Show the requirement banner (only for pilot-station ports). */
      showRequirement?: boolean
      reqState: ArrivalRequirementState
      reqTitle: string
      reqSub: string
    }>(),
    { futureDate: true }
  )

  const emit = defineEmits<{ 'update:modelValue': [value: EtaFieldValue] }>()

  const { emptyEtaField, etaPatchFromTimeChoice, timeChoiceFromEtaField } =
    useEtaField()

  const field = computed<EtaFieldValue>(
    () => props.modelValue ?? emptyEtaField()
  )

  const timeChoice = computed(() => timeChoiceFromEtaField(field.value))

  function patch(changes: Partial<EtaFieldValue>) {
    emit('update:modelValue', { ...field.value, ...changes })
  }

  function onTimeChoice(choice: EstimatedTimeChoice) {
    patch(etaPatchFromTimeChoice(choice))
  }

  // Hint copy keyed off the current precision.
  const isDateOnly = computed(() => field.value.precision === 'date-only')
  const isExact = computed(() => field.value.precision === 'exact-time')
  const isEstimated = computed(() => field.value.precision === 'named-time')

  // Focus the date field when the step opens, keeping the keyboard flow fast
  // (the step component is remounted per step via :key, so this fires on each
  // arrival step).
  const dateWrap = useTemplateRef<HTMLElement>('dateWrap')
  onMounted(async () => {
    await nextTick()
    dateWrap.value?.querySelector('input')?.focus()
  })

  // --- Requirement banner styling ---
  const BANNER = {
    met: {
      box: 'border-emerald-500/30 bg-emerald-500/10',
      iconWrap: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      title: 'text-emerald-700 dark:text-emerald-400',
      icon: IconCheck
    },
    required: {
      box: 'border-amber-500/30 bg-amber-500/10',
      iconWrap: 'bg-amber-500/15 text-amber-600 dark:text-amber-500',
      title: 'text-amber-700 dark:text-amber-500',
      icon: IconWarning
    },
    optional: {
      box: 'border-border bg-muted/50',
      iconWrap: 'bg-muted-foreground/15 text-muted-foreground',
      title: 'text-foreground',
      icon: IconInfo
    }
  } as const
  const banner = computed(() => BANNER[props.reqState])
</script>

<template>
  <div class="flex flex-col gap-5 pt-1">
    <!-- date & time -->
    <div>
      <!-- date field + combined estimated-time input, side by side -->
      <div class="flex flex-wrap items-center gap-3">
        <div ref="dateWrap">
          <InputGroupDate
            class="h-11 w-[248px]"
            :model-value="field.date"
            :disabled="disabled"
            :future-date="futureDate"
            @update:model-value="(v) => patch({ date: v })"
          />
        </div>

        <EstimatedTimePickerCombined
          trigger-class="h-11 w-[200px]"
          :model-value="timeChoice"
          :disabled="disabled"
          @update:model-value="onTimeChoice"
        />
      </div>

      <!-- contextual hint for the chosen precision -->
      <p
        v-if="isDateOnly"
        class="text-muted-foreground mt-2.5 text-xs leading-relaxed"
      >
        Time can be left blank if it's
        <span class="text-foreground font-medium">uncertain</span>.
      </p>
      <p
        v-else-if="isExact"
        class="text-muted-foreground mt-2.5 text-xs leading-relaxed"
      >
        Exact clock time.
      </p>
      <p
        v-else-if="isEstimated"
        class="text-muted-foreground mt-2.5 text-xs leading-relaxed"
      >
        Named approximate window.
      </p>
    </div>

    <!-- requirement banner — only for ports that capture a pilot-station ETA -->
    <div
      v-if="showRequirement"
      :class="['flex items-start gap-3 rounded-lg border p-3', banner.box]"
    >
      <span
        :class="[
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
          banner.iconWrap
        ]"
      >
        <component :is="banner.icon" class="size-3" />
      </span>
      <div class="min-w-0 flex-1">
        <div :class="['text-xs font-semibold', banner.title]">
          {{ reqTitle }}
        </div>
        <div class="text-muted-foreground mt-0.5 text-xs leading-relaxed">
          {{ reqSub }}
        </div>
      </div>
    </div>
  </div>
</template>
